
import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { DateRange } from "react-day-picker"

export interface DatePickerProps {
  date?: Date
  onSelect?: (date: Date | undefined) => void
  onMultiSelect?: (dates: Date[] | undefined) => void
  placeholder?: string
  mode?: "single" | "range" | "multiple"
  selected?: Date | DateRange | Date[] | undefined
  onRangeSelect?: (range: DateRange | undefined) => void
}

export function DatePicker({
  date,
  onSelect,
  onMultiSelect,
  onRangeSelect,
  placeholder = "Pick a date",
  mode = "single",
  selected,
}: DatePickerProps) {
  const formatSelectedDate = () => {
    if (!selected) return <span>{placeholder}</span>;
    
    if (mode === "range" && selected && typeof selected === "object" && "from" in selected) {
      const { from, to } = selected as DateRange;
      if (from && to) {
        return <span>{format(from, "PPP")} - {format(to, "PPP")}</span>;
      } else if (from) {
        return <span>{format(from, "PPP")}</span>;
      }
    }
    
    if (mode === "multiple" && Array.isArray(selected)) {
      if (selected.length === 1) {
        return <span>{format(selected[0], "PPP")}</span>;
      } else if (selected.length > 1) {
        return <span>{selected.length} dates selected</span>;
      }
    }
    
    if (date) {
      return format(date, "PPP");
    }
    
    return <span>{placeholder}</span>;
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className={cn(
            "w-full justify-start text-left font-normal",
            !date && !selected && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {formatSelectedDate()}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        {mode === "range" ? (
          <Calendar
            mode="range"
            selected={selected as DateRange}
            onSelect={onRangeSelect}
            initialFocus
            className={cn("p-3 pointer-events-auto")}
          />
        ) : mode === "multiple" ? (
          <Calendar
            mode="multiple"
            selected={selected as Date[]}
            onSelect={onMultiSelect}
            initialFocus
            className={cn("p-3 pointer-events-auto")}
          />
        ) : (
          <Calendar
            mode="single"
            selected={selected as Date}
            onSelect={onSelect}
            initialFocus
            className={cn("p-3 pointer-events-auto")}
          />
        )}
      </PopoverContent>
    </Popover>
  )
}
